/**
 * D15 (2026-07-08 live-test, Class 7) — "The deep read isn't readable by
 * humans. It needs to be edited and distilled into some meaningful three or
 * four bullet points for each one."
 *
 * RENDER-TIME distillation over ALREADY-GENERATED content (the fix shape
 * Class 7 specifies: "cheapest correct shape is extracting/formatting
 * existing structured fields... only where no structure exists does this
 * need a small additional pass") — this is the "no structure exists" case:
 * job.reasoning is a plain 2-3 sentence paragraph, not a structured field
 * (unlike job.strengths/job.gaps, which are ALREADY arrays and already
 * render as bullet lists — no distillation needed there, they're the
 * pattern this file's job.reasoning treatment is catching up to).
 *
 * Pure, mechanical sentence-splitting — NOT a re-summarization or a new
 * Claude call. The source text stays the ground truth (NO INVENTION: this
 * module reformats, it never rewrites or drops meaning) — the "3-4 bullets"
 * are literally the source's own sentences, capped, with the full original
 * text always available via the caller's expandable (never a lossy summary
 * presented as if it were the complete reasoning).
 */

const MAX_DISTILLED_BULLETS = 4;

export interface DistilledText {
  /** The source's own sentences, capped at MAX_DISTILLED_BULLETS. Never a
   *  paraphrase — literal substrings of `fullText`. */
  bullets: string[];
  /** The complete, un-distilled original text — always available so a
   *  reader can verify the bullets against the full reasoning. */
  fullText: string;
  /** True when fullText carried MORE sentences than the bullet cap — the
   *  caller uses this to decide whether an expandable is worth showing at
   *  all (a 2-sentence reasoning has nothing more to reveal). */
  hasMore: boolean;
}

/** Split on sentence-ending punctuation followed by whitespace — the same
 *  mechanical split OutputRefinementService.ts's detectThirdPersonInText
 *  already uses for exactly this reason (a proven-safe, dependency-free
 *  sentence boundary heuristic for this codebase's short, plain-prose
 *  generation surfaces). */
function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Pure: distill one block of generated prose into its own sentences, capped
 *  at MAX_DISTILLED_BULLETS, alongside the untouched full text. A block with
 *  fewer sentences than the cap distills to ALL of its sentences (nothing
 *  hidden, hasMore: false) — this never pads or invents a 4th point that
 *  isn't in the source. */
export function distillDeepReadText(text: string): DistilledText {
  const fullText = text.trim();
  const sentences = splitSentences(fullText);
  const bullets = sentences.length > 0 ? sentences.slice(0, MAX_DISTILLED_BULLETS) : [];
  return {
    bullets,
    fullText,
    hasMore: sentences.length > MAX_DISTILLED_BULLETS,
  };
}
